/**
 * Returns a list of all required (non choice) IDs for an option
 *
 * @param obj The option to find required items for
 * @returns {*[]|*}
 */
export function getNonChoiceRequired(obj = {}) {
  if (!obj.requires) {
    return [];
  }
  if (Array.isArray(obj.requires)) {
    return obj.requires;
  }
  let arr = obj.requires.AND || [];
  return arr.filter((required) => typeof required === 'string');
}

/**
 * Returns a list of all required (choice) IDs for an option
 *
 * @param obj The option to find required choices for
 * @param lookup An object all possible options mapped to key (ID), value (option).
 *
 * If lookup is not provided, the returned list of choices will be IDs instead
 * of mapped objects.
 *
 * @returns {*[]|*}
 */
export function getChoiceRequired(obj = {}, lookup) {
  // No requires means there can not be any required choices
  // If the requires is an array, all are required so it is not a choice
  if (!obj.requires || Array.isArray(obj.requires)) {
    return [];
  }

  let list = [];

  if (obj.requires.OR) {
    list.push(obj.requires.OR);
  }

  if (obj.requires.AND) {
    list = obj.requires.AND.reduce((accumulator, iterate) => {
      if (typeof iterate === 'object' && iterate !== null && iterate.OR) {
        return [...accumulator, iterate.OR];
      }
      return accumulator;
    }, list);
  }

  return lookup ? list.map((val) => lookup[val]) : list;
}

/**
 * Return the list of precluded options for a given option. There are no choice
 * preclusion's.
 *
 * @param obj The option to find preclusion's for
 * @returns {*[]|*}
 */
export function getPrecluded(obj = {}) {
  if (!obj.precludes) {
    return [];
  }
  if (Array.isArray(obj.precludes)) {
    return obj.precludes;
  }
  return obj.precludes.AND;
}

/**
 * Returns an object of key (option ID), value (option ID) of preclusion's for a
 * given list of options.
 *
 * @param list The list of options to get all preclusion's from.
 * @returns {*}
 */
export function getPrecludedFromList(list = []) {
  return list.reduce((accumulator, iterate) => {
    let precluded = getPrecluded(iterate);
    precluded.forEach((item) => (accumulator[item] = item));
    return accumulator;
  }, {});
}

/**
 * Converts a list of options to an object of key (option ID), value (option)
 *
 * @param list The list of options to map to a list
 * @returns {{}}
 */
function mapListToObject(list = []) {
  let obj = {};
  list.forEach((item) => (obj[item.id] = item));
  return obj;
}

/**
 * Flattens all colours and extras options into an object of key (option ID), value (option)
 *
 * @param coloursAndExtras The full object of all colours and extras
 * @returns {{}}
 */
export function flattenColorsAndExtras(coloursAndExtras = {}) {
  return {
    ...mapListToObject(coloursAndExtras.exteriorColours),
    ...mapListToObject(coloursAndExtras.interiorColours),
    ...mapListToObject(coloursAndExtras.optionalEquipment),
    ...mapListToObject(coloursAndExtras.includedEquipment)
  };
}

/**
 * Return an option matching the given if from the given list
 *
 * @param id The ID to find
 * @param list The list searched for the ID
 * @returns {*}
 */
export function findIdInList(id, list = []) {
  return list.find((equipment) => equipment.id === id);
}

/**
 * Return an index of a matching ID from the given list
 *
 * @param id The ID to find
 * @param list The list searched for the ID
 * @returns {*}
 */
export function findIndexInList(id, list = []) {
  return list.findIndex((equipment) => equipment.id === id);
}

/**
 * Ensures a given id/option is mapped to the lookup values
 *
 * @param idOrOption If String, return the lookup value, otherwise assume an option
 * @param lookup An object all possible options mapped to key (ID), value (option).
 */
export function mapToOption(idOrOption, lookup = {}) {
  return typeof idOrOption === 'string' ? lookup[idOrOption] : idOrOption;
}

/**
 * Return all non choice required for a list of given options and their nested
 * required options.
 *
 * @param requiredList The list of options to find required options for
 * @param lookup An object all possible options mapped to key (ID), value (option).
 * @returns {{}}
 */
export function getAllRequired(requiredList = [], lookup = {}) {
  let fullRequirements = {};

  requiredList.forEach((requirement) => {
    let matchedItem = mapToOption(requirement, lookup);
    if (matchedItem) {
      fullRequirements = {
        ...fullRequirements,
        [matchedItem.id]: matchedItem,
        ...getAllRequired(getNonChoiceRequired(matchedItem), lookup)
      };
    }
  });
  return fullRequirements;
}
